import { AgentHubGetOffersCommissionBody } from 'agentHub/api/agentHubCommission'
import { mapOfferToAgentHubCommissionPayload } from 'agentHub/api/mappers/commissionMap'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { AGENT_HUB_PAYMENT_MODE } from 'constants/payment'
import { groupBy } from 'lib/array/arrayUtils'
import { isBedbankOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import { createSelector } from 'reselect'

export const isAgentHubEnabled = agentHubOrSubBrandConfig.isEnabled
export const isAgentHubCVFREnabled = agentHubOrSubBrandConfig.isCVFREnabled

const getAgentHubOfferPackages = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOffers[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOfferRates[offerId],
  (offer, bedbankOffer, offerRates) => {
    const rateKey = Object.keys(offerRates ?? {})[0]
    if (bedbankOffer || isBedbankOffer(offer)) {
      if (!offerRates?.[rateKey]) {
        return []
      }

      return offerRates[rateKey].map(rate => {
        return {
          packageId: rate.id,
          mx: rate.mx || '',
        }
      })
    }

    if (!offer?.packages) {
      return []
    }

    if (isLpcHotelOffer(offer)) {
      return offer.packages.filter(pkg => pkg.roomRate?.mx).map(pkg => {
        return {
          packageId: pkg.id,
          mx: pkg.roomRate?.mx ?? '',
        }
      })
    }

    return offer.packages.filter(pkg => pkg.mx).map(pkg => ({
      packageId: pkg.id,
      mx: pkg.mx || '',
    }))
  },
)

export const getAgentHubOfferCommissionBody = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOffers[offerId],
  (state: App.State, offerId: string) => state.offer.offers[`cruise:${offerId}`],
  getAgentHubOfferPackages,
  (offer, bedbankOffer, cruiseOffer, packages): AgentHubGetOffersCommissionBody | null => {
    const offerData = offer || bedbankOffer || cruiseOffer

    if (!offerData) {
      return null
    }

    const baseOfferBody = mapOfferToAgentHubCommissionPayload(offerData)

    const offerBody = {
      ...baseOfferBody,
      packages,
    }

    return offerBody
  },
)

export const getAgentHubOfferCommissionBodyForBundledFlow = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State, flightWithJourney: App.FlightWithJourney) => flightWithJourney,
  (cartItems, flightWithJourney) => {
    const groupedCartItems = groupBy(cartItems, item => item.itemId, item => (item as App.Checkout.AccommodationItem).offerId)
    const bundledOffers = flightWithJourney.flight.bundledItemIds?.filter(id => groupedCartItems.has(id)).flatMap(id => groupedCartItems.get(id) ?? [])
    return mapOfferToAgentHubCommissionPayload({ flight: flightWithJourney.flight, journey: flightWithJourney.journey }, { bundledOffers })
  }
)

export const getIsAgentHubGrossPayment = createSelector(
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (agentHubPaymentMode) => {
    return isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_GROSS_AMOUNT
  },
)

const defaultAffiliationRules: Required<NonNullable<App.AgentHubAccount['affiliationRules']>> = {
  canBookCarHire: true,
  canBookCruise: true,
}

export const getAgentHubAffiliationRules = createSelector(
  (state: App.State) => state.agentHub?.agentHubAccount?.account?.affiliationRules,
  (affiliationRules) =>
  {
    return { ...defaultAffiliationRules, ...(affiliationRules ?? {}) } as Required<NonNullable<App.AgentHubAccount['affiliationRules']>>
  },
)

export const getAgentHubTier = (state: App.State) => state.agentHub.agentHubAccount.account?.agentTier

export const getCanAgentExportMedia = isAgentHubEnabled
export const isAgentHubAffiliationRequired = isAgentHubEnabled && !isAgentHubCVFREnabled
